import { CloseOutlined } from '@ant-design/icons';
import { Button, Form, Input, Modal, Radio, Space } from 'antd';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from '../../../../app/store';
import { TrainingDataItem } from '../../Training';
import './createTrainingData.scss';

const { TextArea } = Input;


interface CreateTrainingDataProps {
  visible: boolean;
  onCancel: () => void;
  onSave: (data: TrainingDataItem, isEditing: boolean) => void;
  editData?: TrainingDataItem;
}

const CreateTrainingData: React.FC<CreateTrainingDataProps> = ({ visible, onCancel, onSave, editData }) => {
  const { isLoading: isLoadingInitiateTraining } = useSelector((state: RootState) => state.training.initiateTraining)
  const { isLoading: isLoadingUpdateTraining, isSuccess: isSuccessUpdateTraining } = useSelector((state: RootState) => state.training.updateTraining)

  const [form] = Form.useForm();
  const isEditing = !!editData;
  
  // Watch the type field to conditionally render fields
  const selectedType = Form.useWatch('type', form);
  

  useEffect(() => {
    if (visible && editData) {
      form.setFieldsValue({
        type: editData.type,
        sql: editData.sql,
        question: editData.question
      });

    } else if (visible) {
      form.resetFields();

    }
  }, [visible, editData, form]);

  const handleSave = () => {
    form
      .validateFields()
      .then(values => {
        const data: any = {
          ...values,
        };

        if (isEditing && editData?.id) {
          data.training_id = editData.id.toString();
        }

        onSave(data, isEditing);
        form.resetFields();
      })
      .catch(info => {
        console.warn('Validation Failed:', info);
      });
  };

  // Function to get field label based on selected type
  const getFieldLabel = () => {
    switch (selectedType) {
      case 'ddl':
        return 'Your DDL';
      case 'documentation':
        return 'Your Documentation';
      case 'sql':
      default:
        return 'Your SQL';
    }
  };

  // Function to get placeholder text based on selected type
  const getPlaceholder = () => {
    switch (selectedType) {
      case 'ddl':
        return 'CREATE TABLE table_name (column1 datatype, column2 datatype, ...);';
      case 'documentation':
        return 'Enter your documentation here...';
      case 'sql':
      default:
        return 'SELECT column_1, column_2 FROM table_name;';
    }
  };


  const nameKeyMap: Record<string, string> = {
    ddl: 'ddl',
    documentation: 'documentation',
    sql: 'sql',
  };

  const getNameKey = () => nameKeyMap[selectedType] || 'sql';

  return (
    <Modal title="Add Training Data" open={visible} onCancel={onCancel} footer={null} width={650} closeIcon={<CloseOutlined />} className="create-training-data-modal">
      <Form form={form} layout="vertical" className='form-container' initialValues={{ type: 'sql' }}>
        <div className="training-data-type-section">
          <h3>Training Data Type</h3>
          <Form.Item name="type" className="radio-group-item">
            <Radio.Group>
              <Space direction="vertical" >
                <Radio value="ddl">
                  <div className="radio-option">
                    <span className="option-title">DDL</span>
                    <span className="option-description">
                      These are the CREATE TABLE statements that define your database structure.
                    </span>
                  </div>
                </Radio>
                <Radio value="documentation">
                  <div className="radio-option">
                    <span className="option-title">Documentation</span>
                    <span className="option-description">
                      This can be any text-based documentation. Keep the chunks small and focused on a single topic.
                    </span>
                  </div>
                </Radio>
                <Radio value="sql">
                  <div className="radio-option">
                    <span className="option-title">SQL</span>
                    <span className="option-description">
                      This can be any SQL statement that works. The more the merrier.
                    </span>
                  </div>
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
        </div>

        <Form.Item
          label={getFieldLabel()}
          name={getNameKey()}
          rules={[{ required: true, message: `Please enter your ${selectedType === 'ddl' ? 'DDL' : selectedType === 'documentation' ? 'documentation' : 'SQL'} content` }]}
        >
          <TextArea
            rows={4}
            placeholder={getPlaceholder()}
            className="sql-textarea"
          />
        </Form.Item>

        {/* Only show Question field for SQL type */}
        {selectedType === 'sql' && (
          <Form.Item
            label="Question"
            name="question"
            rules={[{ required: true, message: 'Please enter a question' }]}
          >
            <TextArea
              rows={3}
              placeholder="Question"
              className="question-textarea"
            />
          </Form.Item>
        )}

        <Form.Item className="save-button-container">
          <Button type="primary" onClick={handleSave} block className="save-button">
            {isLoadingInitiateTraining ? 'Saving...' : isEditing ? isLoadingUpdateTraining ? 'Updating...' : 'Update' : 'Save'}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateTrainingData;